import React from "react";
import { Handle, Position } from "reactflow";
import { Typography, useTheme } from "@mui/material";

interface InputOutputHandleProps {
  /** "input", "output", or "both" */
  type: "input" | "output" | "both";

  /** Unique id for React Flow handle */
  id: string | number;

  /** Optional label or [inputLabel, outputLabel] for "both" mode */
  label?: string | [string, string] | null;

  /** Optional color or [inputColor, outputColor] for "both" mode */
  handleColor?: string | [string, string] | null;

  /** Whether the handle can connect */
  isConnectable?: boolean;
}

/**
 * Unified component for input, output, or both handles.
 * Backward compatible, supports optional labels, and “both” mode.
 */
const InputOutputHandle: React.FC<InputOutputHandleProps> = ({
  type,
  id,
  label,
  handleColor,
  isConnectable = true,
}) => {
  const theme = useTheme();
  const safeBackground = theme.custom.nodes.background.main.dark || "#ffffff";

  // --- Helper to extract left/right (input/output) values ---
  const getValue = <T,>(value: T | [T, T] | null | undefined, index: 0 | 1): T | null => {
    if (Array.isArray(value)) return value[index] ?? value[0];
    return (value as T) ?? null;
  };

  const inputLabel = getValue(label, 0);
  const outputLabel = getValue(label, 1);
  const inputColor = getValue(handleColor, 0) || "#000000";
  const outputColor = getValue(handleColor, 1) || "#000000";

  // --- Handle style helper ---
  const handleStyle = (color: string) => ({
    background: color,
    border: "1px solid #1c3c57",
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    top: "50%",
  });

  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          type === "output" ? "flex-end" : type === "input" ? "flex-start" : "space-between",
        alignItems: "center",
        background: safeBackground,
        color: theme.custom.nodes.text.primary.dark,
        fontSize: "12px",
        fontWeight: "bold",
        padding: "6px 10px",
        position: "relative",
        gap: "6px",
        width: "100%",
        minHeight: "30px", // ✅ Ensures div never collapses completely
      }}
    >
      {/* Input handle (for input or both) */}
      {(type === "input" || type === "both") && (
        <>
          <Handle
            id={`input-${id}`}
            type="target"
            position={Position.Left}
            isConnectable={isConnectable}
            style={handleStyle(inputColor)}
          />

          {inputLabel && (
            <Typography
              variant="body2"
              component="span"
              style={{
                textAlign: "left",
                whiteSpace: "nowrap",
                flex: type === "both" ? 1 : undefined,
              }}
            >
              {inputLabel}
            </Typography>
          )}
        </>
      )}

      {/* Output handle (for output or both) */}
      {(type === "output" || type === "both") && (
        <>
          {outputLabel && (
            <Typography
              variant="body2"
              component="span"
              style={{
                flex: type === "both" ? 1 : undefined,
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              {outputLabel}
            </Typography>
          )}

          <Handle
            id={`output-${id}`}
            type="source"
            position={Position.Right}
            isConnectable={isConnectable}
            style={handleStyle(outputColor)}
          />
        </>
      )}
    </div>
  );
};

export default InputOutputHandle;
